import { Link, useParams } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Twitter, Heart, Coffee } from "lucide-react";
import { useStore } from "../context/Store";
import { PATTERNS, t } from "../i18n";
import TrustStrip from "./TrustStrip";
import { openCookiePreferences } from "./CookieBanner";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M15 3v10.4a3.6 3.6 0 1 1-3.6-3.6h.6V7a6.8 6.8 0 1 0 6.8 6.8V8.7c1.3.9 2.8 1.4 4.2 1.4V6.9c-2.6-.1-4.7-1.7-5.4-3.9H15z" />
  </svg>
);
const SnapIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2c3.6 0 5.6 2.8 5.5 6l-.1 2.6c.8.1 1.7.7 2.1 1.1.5.4.7.9.3 1.4-.4.5-1.5.7-2.4 1.2-.9.4-.8 1-.4 1.8.4.9 1.6 1.9 3.1 2.4.5.2.7.6.4 1-.4.5-1.6.8-2.6.9-.7.1-.8.6-.9 1.1-.1.7-.9.6-1.8.5-.9-.1-1.9.1-2.8.8-.9.7-1.5 1-2.4 1s-1.5-.3-2.4-1c-.9-.7-1.9-.9-2.8-.8-.9.1-1.7.2-1.8-.5-.1-.5-.2-1-.9-1.1-1-.1-2.2-.4-2.6-.9-.3-.4-.1-.8.4-1 1.5-.5 2.7-1.5 3.1-2.4.4-.8.5-1.4-.4-1.8-.9-.5-2-.7-2.4-1.2-.4-.5-.2-1 .3-1.4.4-.4 1.3-1 2.1-1.1L6.5 8C6.4 4.8 8.4 2 12 2z"/>
  </svg>
);
const PinterestIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.5 2 2 6.5 2 12c0 4.2 2.6 7.7 6.3 9.2-.1-.8-.2-2 0-2.9.2-.8 1.3-4.9 1.3-4.9s-.3-.7-.3-1.7c0-1.6.9-2.7 2.1-2.7 1 0 1.5.7 1.5 1.6 0 1-.6 2.5-1 3.9-.3 1.1.6 2 1.7 2 2 0 3.5-2.1 3.5-5.1 0-2.7-1.9-4.6-4.6-4.6-3.1 0-5 2.3-5 4.7 0 .9.4 1.9.8 2.5.1.1.1.2.1.3 0 .1-.1.4-.1.5 0 .1-.1.2-.3.1-1.1-.5-1.8-2.1-1.8-3.4 0-2.8 2-5.3 5.9-5.3 3.1 0 5.5 2.2 5.5 5.2 0 3.1-2 5.6-4.7 5.6-.9 0-1.8-.5-2.1-1l-.6 2.2c-.2.8-.8 1.9-1.2 2.5.9.3 1.9.4 2.9.4 5.5 0 10-4.5 10-10S17.5 2 12 2z"/>
  </svg>
);
const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.5 3.5A10.5 10.5 0 0 0 3.6 17L2 22l5.1-1.5A10.5 10.5 0 1 0 20.5 3.5zM12 20.4a8.4 8.4 0 0 1-4.3-1.2l-.3-.2-3 .9.9-3-.2-.3A8.4 8.4 0 1 1 12 20.4zm4.6-6.2c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1s-.7.8-.8 1c-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.2-1.3-.8-.7-1.4-1.6-1.5-1.9-.2-.3 0-.5.1-.6.1-.1.3-.4.5-.6.1-.2.2-.3.3-.5.1-.2.1-.4 0-.5-.1-.2-.6-1.5-.8-2.1-.2-.5-.4-.5-.6-.5h-.5c-.2 0-.5.1-.7.3-.2.2-1 1-1 2.3s1 2.7 1.2 2.9c.1.2 2.1 3.2 5.1 4.4.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.3-.2-.6-.3z"/>
  </svg>
);

const LEGAL_LABELS = {
  en: { use: "Terms of use", delivery: "Delivery policy", c1: "Made with", c2: "and", c3: "by", kw: "Web Agency Tangier", prefs: "Cookie preferences", accept: "WE ACCEPT" },
  fr: { use: "Conditions d'utilisation", delivery: "Politique de livraison", c1: "Conçu avec", c2: "et", c3: "par", kw: "Agence Web Tanger", prefs: "Préférences cookies", accept: "NOUS ACCEPTONS" },
  es: { use: "Condiciones de uso", delivery: "Política de entrega", c1: "Hecho con", c2: "y", c3: "por", kw: "Agencia Web Tánger", prefs: "Preferencias de cookies", accept: "ACEPTAMOS" },
  ar: { use: "شروط الاستخدام", delivery: "سياسة التوصيل", c1: "صُنع بـ", c2: "و", c3: "من", kw: "وكالة ويب طنجة", prefs: "تفضيلات ملفات الارتباط", accept: "نقبل" },
  it: { use: "Condizioni d'uso", delivery: "Politica di consegna", c1: "Fatto con", c2: "e", c3: "da", kw: "Agenzia Web Tangeri", prefs: "Preferenze cookie", accept: "ACCETTIAMO" },
  nl: { use: "Gebruiksvoorwaarden", delivery: "Bezorgbeleid", c1: "Gemaakt met", c2: "en", c3: "door", kw: "Webbureau Tanger", prefs: "Cookievoorkeuren", accept: "WIJ ACCEPTEREN" },
  de: { use: "Nutzungsbedingungen", delivery: "Lieferrichtlinie", c1: "Gemacht mit", c2: "und", c3: "von", kw: "Webagentur Tanger", prefs: "Cookie-Einstellungen", accept: "WIR AKZEPTIEREN" },
};

const PAYMENTS = [
  { src: "/payments/stripe.png", alt: "Stripe", chip: false, h: 18 },
  { src: "/payments/visa.png", alt: "Visa", chip: false, h: 20 },
  { src: "/payments/mastercard.png", alt: "Mastercard", chip: false, h: 26 },
  { src: "/payments/bizum.png", alt: "Bizum", chip: false, h: 20 },
  { src: "/payments/bunq.png", alt: "bunq", chip: false, h: 16 },
  { src: "/payments/applepay.webp", alt: "Apple Pay", chip: true, h: 18 },
  { src: "/payments/gpay.png?v=2", alt: "Google Pay", chip: true, h: 16 },
];

const SOCIAL_LIST = [
  ["instagram", Instagram, "Instagram"],
  ["facebook", Facebook, "Facebook"],
  ["tiktok", TikTokIcon, "TikTok"],
  ["youtube", Youtube, "YouTube"],
  ["linkedin", Linkedin, "LinkedIn"],
  ["snapchat", SnapIcon, "Snapchat"],
  ["x", Twitter, "X (Twitter)"],
  ["pinterest", PinterestIcon, "Pinterest"],
  ["whatsapp_channel", WhatsAppIcon, "WhatsApp"],
];

export const Footer = () => {
  const { locale = "en" } = useParams();
  const { boot } = useStore();
  const pat = PATTERNS[locale] || PATTERNS.en;
  const socials = boot?.socials || {};
  const activeSocials = SOCIAL_LIST.filter(([key]) => (socials[key] || "").trim());
  return (
    <>
      <TrustStrip locale={locale} />
      <footer className="bg-ink text-plaster" data-testid="site-footer">
        <div className="max-w-content mx-auto px-6 pt-12 pb-2 flex justify-center md:justify-start">
          <Link to={`/${locale}`} aria-label="Fleurinelle" data-testid="footer-logo-link" className="inline-block">
            <img
              src="/brand/fleurinelle-header-dark.png"
              alt="Fleurinelle"
              width="1200"
              height="360"
              className="h-11 md:h-12 w-auto block opacity-95 select-none"
              draggable="false"
            />
          </Link>
        </div>
        <div className="max-w-content mx-auto px-6 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h4 className="smallcaps text-brass-light mb-4">{t(locale, "cities")}</h4>
            <ul className="space-y-2 text-sm">
              {(boot?.cities || []).map((c) => (
                <li key={c.id}>
                  <Link to={`/${locale}/${pat.city}/${c.slug}`} className="hover:text-brass-light transition-colors duration-200">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="smallcaps text-brass-light mb-4">{t(locale, "occasions")}</h4>
            <ul className="space-y-2 text-sm">
              {(boot?.occasions || []).slice(0, 10).map((o) => (
                <li key={o.id}>
                  <Link to={`/${locale}/${pat.occasion}/${o.slug}`} className="hover:text-brass-light transition-colors duration-200">
                    {o.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="smallcaps text-brass-light mb-4">{t(locale, "company")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to={`/${locale}/about`}>{t(locale, "about")}</Link></li>
              <li><Link to={`/${locale}/how-it-works`}>{t(locale, "howItWorksLink")}</Link></li>
              <li><Link to={`/${locale}/florist/apply`} data-testid="footer-become-partner">{t(locale, "becomePartner")}</Link></li>
              <li><Link to={`/${locale}/driver/apply`} data-testid="footer-become-driver">{t(locale, "becomeDriver")}</Link></li>
              <li><Link to={`/${locale}/contact`}>{t(locale, "contact")}</Link></li>
              <li><Link to={`/${locale}/track`}>{t(locale, "track")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="smallcaps text-brass-light mb-4">{t(locale, "legal")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to={`/${locale}/terms`}>{t(locale, "terms")}</Link></li>
              <li><Link to={`/${locale}/terms-of-use`}>{LEGAL_LABELS[locale]?.use || LEGAL_LABELS.en.use}</Link></li>
              <li><Link to={`/${locale}/privacy`}>{t(locale, "privacy")}</Link></li>
              <li><Link to={`/${locale}/refunds`}>{t(locale, "refundPolicy")}</Link></li>
              <li><Link to={`/${locale}/delivery`}>{LEGAL_LABELS[locale]?.delivery || LEGAL_LABELS.en.delivery}</Link></li>
              <li><Link to={`/${locale}/cookies`}>{t(locale, "cookies")}</Link></li>
              <li>
                <button type="button" data-testid="cookie-preferences-link"
                        onClick={openCookiePreferences}
                        className="hover:text-brass-light transition-colors duration-200 text-start">
                  {LEGAL_LABELS[locale]?.prefs || LEGAL_LABELS.en.prefs}
                </button>
              </li>
              <li><Link to={`/${locale}/substitution-policy`}>{t(locale, "substitution")}</Link></li>
              <li><Link to={`/${locale}/delivery-guarantee`}>{t(locale, "guarantee")}</Link></li>
            </ul>
          </div>
        </div>

        {activeSocials.length > 0 ? (
          <div className="border-t border-ink-soft">
            <div className="max-w-content mx-auto px-6 py-6 flex items-center gap-4 flex-wrap">
              <span className="smallcaps text-brass-light">{t(locale, "followUs")}</span>
              <div className="flex gap-3" data-testid="footer-socials">
                {activeSocials.map(([key, Icon, label]) => (
                  <a key={key} href={socials[key]} target="_blank" rel="noreferrer noopener me"
                     aria-label={label} title={label}
                     data-testid={`footer-social-${key}`}
                     className="w-9 h-9 flex items-center justify-center border border-plaster/30 rounded-full hover:bg-brass hover:border-brass hover:text-ink transition-colors">
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="border-t border-ink-soft">
          <div className="max-w-content mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-testid="payment-logos">
            <span className="text-[11px] tracking-[0.22em] text-plaster/45 uppercase">{(LEGAL_LABELS[locale] || LEGAL_LABELS.en).accept}</span>
            {PAYMENTS.map((p) => p.chip ? (
              <span key={p.alt} title={p.alt} className="bg-white rounded-[4px] px-2 py-1 inline-flex items-center" data-testid={`pay-${p.alt.replace(/\s+/g, "-").toLowerCase()}`}>
                <img src={p.src} alt={p.alt} style={{ height: p.h }} className="block w-auto" />
              </span>
            ) : (
              <img key={p.alt} src={p.src} alt={p.alt} title={p.alt} style={{ height: p.h }}
                   data-testid={`pay-${p.alt.replace(/\s+/g, "-").toLowerCase()}`}
                   className="block w-auto opacity-75" />
            ))}
          </div>
        </div>

        <div className="border-t border-ink-soft">
          <div className="max-w-content mx-auto px-6 py-6 flex flex-col md:flex-row gap-4 justify-between text-xs text-plaster/70">
            <p data-testid="footer-legal">
              Fleurinelle · {boot?.support?.legal_name || "TRYB International, LLC"}, {boot?.support?.legal_address || "131 Continental Dr, Suite 305, Newark, DE 19713, United States"}
              {boot?.support?.vat_id ? ` · VAT ${boot.support.vat_id}` : ""}
            </p>
            {boot?.support?.support_whatsapp ? (
              <a href={`https://wa.me/${boot.support.support_whatsapp.replace(/\D/g, "")}`} className="hover:text-brass-light" data-testid="whatsapp-support-link">
                {t(locale, "support")} · {boot.support.support_whatsapp} · {boot.support.support_hours}
              </a>
            ) : (
              <a href={`mailto:${boot?.support?.support_email || "hello@fleurinelle.com"}`} className="hover:text-brass-light" data-testid="email-support-link">
                {t(locale, "support")} · {boot?.support?.support_email || "hello@fleurinelle.com"} · {boot?.support?.support_hours || "09:00 – 21:00 (GMT+1)"}
              </a>
            )}
          </div>
        </div>

        <div className="border-t border-ink-soft">
          <div className="max-w-content mx-auto px-6 py-4 text-center text-xs text-plaster/55" data-testid="footer-credit">
            <span className="inline-flex items-center gap-1.5 flex-wrap justify-center" dir={locale === "ar" ? "rtl" : "ltr"}>
              {(LEGAL_LABELS[locale] || LEGAL_LABELS.en).c1}
              <Heart size={13} className="text-brass-light fill-brass-light" aria-label="love" />
              {(LEGAL_LABELS[locale] || LEGAL_LABELS.en).c2}
              <Coffee size={13} className="text-brass-light" aria-label="coffee" />
              {(LEGAL_LABELS[locale] || LEGAL_LABELS.en).c3}
              <a href="https://crescents.ma" target="_blank" rel="noreferrer noopener"
                 title={(LEGAL_LABELS[locale] || LEGAL_LABELS.en).kw}
                 data-testid="crescents-credit"
                 className="text-brass-light hover:text-brass underline underline-offset-2 transition-colors">
                CRESCENTS
              </a>
            </span>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
